// src/pages/StaffLogin.tsx
import { KeyRound, User } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardHeader,
  CardTitle,
  Note,
} from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Field, InputWithIcon } from '../components/ui/input'
import { ApiError, post, rateLimitMessage } from '../lib/api'
import { saveStaffSession, type StaffProfile } from '../lib/staff'
import AuthLayout from '../components/AuthLayout'

interface LoginResponse {
  status: 'signed_in'
  token: string
  expiresAt: string
  role: StaffProfile['role']
  name: string | null
  username: string
  mustChangePassword: boolean
  booth: StaffProfile['booth']
  judge: StaffProfile['judge']
}

const HOME: Record<StaffProfile['role'], string> = {
  booth_manager: '/booth',
  judge: '/judge',
  admin: '/admin',
  mc: '/mc',
}

// Same compact styling as the student pages
const inputClass =
  'h-11 rounded-lg border-slate-200 bg-white text-sm focus:border-blue-500 focus:ring-blue-500/20'

export default function StaffLogin() {
  const navigate = useNavigate()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!username.trim() || !password) {
      setError('Enter your user id and password.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const res = await post<LoginResponse>(
        '/api/staff/login',
        { username: username.trim().toLowerCase(), password },
        'none',
      )

      saveStaffSession(res.token, res.expiresAt, {
        role: res.role,
        username: res.username,
        name: res.name,
        mustChangePassword: res.mustChangePassword,
        booth: res.booth,
        judge: res.judge,
      })

      navigate(HOME[res.role] ?? '/', { replace: true })
    } catch (err) {
      const apiErr = err as ApiError
      const detail = apiErr.detail as { minutesLeft?: number } | undefined

      setError(
        rateLimitMessage(err) ??
        (apiErr.message === 'locked'
          ? `Too many attempts. Try again in ${detail?.minutesLeft ?? 15} minutes, or ask Admin to reset it.`
          : apiErr.message === 'invalid_credentials'
            ? 'That user id and password do not match.'
            : 'Something went wrong. Please try again.'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-sm rounded-2xl border border-white bg-white/90 text-slate-900 shadow-xl shadow-blue-900/10">
        <CardHeader className="space-y-1 px-6 pb-2 pt-6">
          <CardEyebrow className="text-[11px] font-bold uppercase tracking-widest text-orange-600">
            Staff sign-in
          </CardEyebrow>
          <CardTitle className="text-2xl font-bold text-[#0B1E3F]">Sign in to your portal</CardTitle>
          <CardDescription className="text-sm text-slate-500">
            Booth Managers, Judges and the Committee. Use the user id and password the Committee
            sent you — you will be taken to the right screen automatically.
          </CardDescription>
        </CardHeader>

        <CardContent className="px-6 pb-6 pt-3">
          <form onSubmit={onSubmit} noValidate className="space-y-3.5">
            {/*
              "User id" rather than "Username": a booth manager's is their work
              e-mail address, and they are all but two of the people who sign in
              here. Admin and the judges keep short handles, which the label
              still covers.
            */}
            <Field label="User ID" htmlFor="staff-user">
              <InputWithIcon
                icon={User}
                id="staff-user"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value)
                  setError(null)
                }}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="Your e-mail address"
                className={inputClass}
              />
            </Field>

            <Field label="Password" htmlFor="staff-pass">
              <InputWithIcon
                icon={KeyRound}
                id="staff-pass"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(null)
                }}
                placeholder="Please enter your password"
                autoComplete="current-password"
                className={inputClass}
              />
            </Field>

            {error && (
              <Note tone="error" className="rounded-lg border-red-200 bg-red-50/80 text-xs text-red-700">
                {error}
              </Note>
            )}

            <Button
              type="submit"
              block
              disabled={submitting}
              className="h-11 rounded-lg bg-orange-500 py-0 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 disabled:opacity-70"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <Note className="mt-4 rounded-lg border-blue-100 bg-blue-50/80 text-xs text-slate-600">
            You stay signed in for the day. Students do not sign in here — they register on the
            event page and reopen their pass from the link in their e-mail.
          </Note>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
